import {
  Slide,
  SlideImage as SlideImageType,
  TextAlignment,
} from '@/client/courses';
import { EditableText } from '@/ui/editable-text/EditableText';
import { FlexContainer } from '@/ui/styled-ui';
import { Image } from 'primereact/image';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const SlideImagePreview = styled(FlexContainer)<{
  addBorder?: boolean;
  textAlignment?: TextAlignment;
  isGallery?: boolean;
}>`
  flex: ${({ isGallery }) => (isGallery ? '0 0 auto' : '1')};
  width: 100%;
  height: ${({ isGallery }) => (isGallery ? '220px' : 'auto')};
  max-height: ${({ isGallery }) => (isGallery ? '220px' : 'auto')};
  max-width: 100%;
  align-items: center;
  justify-content: ${({ textAlignment }) =>
    textAlignment === TextAlignment.CENTER
      ? 'center'
      : textAlignment === TextAlignment.RIGHT
      ? 'flex-end'
      : 'flex-start'};
  flex-shrink: 0;
  position: relative;
  min-width: 0;
  border: none !important;
  padding: 0;
  background: transparent;

  .p-image {
    display: inline-flex !important;
    align-items: center;
    justify-content: ${({ textAlignment }) =>
      textAlignment === TextAlignment.CENTER
        ? 'center'
        : textAlignment === TextAlignment.RIGHT
        ? 'flex-end'
        : 'flex-start'};
    width: auto !important;
    height: auto !important;
    max-width: 100%;
    max-height: ${({ isGallery }) => (isGallery ? '220px' : 'auto')};
    border: none !important;
    padding: 0 !important;
    background: transparent !important;
  }

  .p-image img {
    border-radius: var(--medium-border-radius);
    border: ${({ addBorder }) =>
      addBorder ? '4px solid var(--gray-moderate)' : 'none'} !important;
    width: auto !important;
    max-width: 100%;
    height: auto !important;
    max-height: ${({ isGallery }) => (isGallery ? '220px' : 'auto')};
    object-fit: contain;
    object-position: ${({ textAlignment }) =>
      textAlignment === TextAlignment.CENTER
        ? 'center'
        : textAlignment === TextAlignment.RIGHT
        ? 'right'
        : 'left'};
    display: block;
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  button {
    border-radius: var(--medium-border-radius);
  }
`;

const StyledTextContainer = styled(FlexContainer)<{
  textAlignment?: TextAlignment;
}>`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  flex: 1 0 auto;
  flex-grow: 0;
  align-content: ${({ textAlignment }) =>
    textAlignment === TextAlignment.CENTER
      ? 'center'
      : textAlignment === TextAlignment.RIGHT
      ? 'flex-end'
      : 'flex-start'};

  h3 {
    text-align: ${({ textAlignment }) => textAlignment};
    font-size: var(--medium-font-size);
    font-weight: 700;
    line-height: var(--medium-line-height);
    color: var(--black-main);
    margin: 0px;
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }

  p {
    text-align: ${({ textAlignment }) => textAlignment};
    font-size: var(--medium-font-size);
    font-weight: 400;
    line-height: var(--medium-line-height);
    color: var(--black-main);
    margin: 0px;
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }
`;

const EditableFieldWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  min-width: 0;
`;

const ExcludedTextWrapper = styled.div`
  width: 100%;
`;

const SlideImageComponentContainer = styled.div<{
  textAlignment: TextAlignment;
  isGallery?: boolean;
  isSummary?: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: ${({ textAlignment }) =>
    textAlignment === TextAlignment.CENTER
      ? 'center'
      : textAlignment === TextAlignment.RIGHT
      ? 'flex-end'
      : 'flex-start'};
  gap: var(--small-padding);
  width: 100%;
  max-width: ${({ isGallery }) => (isGallery ? '100%' : '375px')};
  min-width: 0;
  justify-content: flex-start;
  height: 100%;

  h3,
  p {
    color: ${({ isSummary }) =>
      isSummary ? 'var(--white-main)' : 'var(--black-main)'};
  }
`;

type SlideImageComponentProps = {
  image: SlideImageType;
  currentEditingSlide?: Slide;
  updateSlide?: (id: string, updates: Partial<Slide>) => void;
  textAlignment?: TextAlignment;
  isGallery?: boolean;
  isSummary?: boolean;
};

export const SlideImageComponent: React.FC<SlideImageComponentProps> = ({
  image,
  currentEditingSlide,
  updateSlide,
  textAlignment = TextAlignment.LEFT,
  isGallery = false,
  isSummary = false,
}) => {
  const { t } = useTranslation();

  return (
    <SlideImageComponentContainer
      textAlignment={textAlignment}
      isGallery={isGallery}
      isSummary={isSummary}
    >
      {image.file?.signedUrl && (
        <SlideImagePreview
          addBorder={image.withBorder || false}
          textAlignment={textAlignment}
          isGallery={isGallery}
        >
          <Image
            src={image.file.signedUrl}
            preview={image.withZoom || false}
            loading="lazy"
          />
        </SlideImagePreview>
      )}
      <StyledTextContainer
        direction="column"
        align="stretch"
        justify="flex-start"
        className="w-full"
        textAlignment={textAlignment}
      >
        {updateSlide && currentEditingSlide ? (
          <>
            <ExcludedTextWrapper>
              <EditableFieldWrapper>
                <EditableText
                  multiline
                  textAlignment={textAlignment}
                  isBold
                  value={image.title || t('content.image.text')}
                  saveValue={(value: string) => {
                    if (!currentEditingSlide?.id) return;
                    updateSlide(currentEditingSlide.id, {
                      images: currentEditingSlide.images?.map((img) =>
                        img.position === image.position
                          ? {
                              ...img,
                              title:
                                value === t('content.image.text')
                                  ? undefined
                                  : value,
                            }
                          : img,
                      ),
                    });
                  }}
                />
              </EditableFieldWrapper>
              <EditableFieldWrapper>
                <EditableText
                  multiline
                  textAlignment={textAlignment}
                  value={image.description || t('content.image.text')}
                  saveValue={(value: string) => {
                    if (!currentEditingSlide?.id) return;
                    updateSlide(currentEditingSlide.id, {
                      images: currentEditingSlide.images?.map((img) =>
                        img.position === image.position
                          ? {
                              ...img,
                              description:
                                value === t('content.image.text')
                                  ? undefined
                                  : value,
                            }
                          : img,
                      ),
                    });
                  }}
                />
              </EditableFieldWrapper>
            </ExcludedTextWrapper>
          </>
        ) : (
          <>
            {image.title && <h3>{image.title}</h3>}
            {image.description && <p>{image.description}</p>}
          </>
        )}
      </StyledTextContainer>
    </SlideImageComponentContainer>
  );
};
